/**
 * Pure helpers for the portfolio overview: account-table sorting and last-active
 * relative formatting. Kept dependency-free so they're trivially unit-testable.
 */
import type { AccountSummary } from '@/types/overview';

export type SortKey = 'volume' | 'rating' | 'trend';

/**
 * Sort accounts for the overview table. Descending by default (most first).
 * `trend` sorts by ratingDelta with nulls last. Non-mutating.
 */
export function sortAccounts(
  accounts: AccountSummary[],
  key: SortKey,
  direction: 'asc' | 'desc' = 'desc'
): AccountSummary[] {
  const dir = direction === 'asc' ? 1 : -1;
  return [...accounts].sort((a, b) => {
    if (key === 'volume') return (a.totalFeedback - b.totalFeedback) * dir;
    if (key === 'rating') return (a.averageRating - b.averageRating) * dir;
    // trend: nulls always sort last regardless of direction
    const av = a.ratingDelta;
    const bv = b.ratingDelta;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * dir;
  });
}

/**
 * Human "last active" label from a yyyy-MM-dd day (overview granularity is daily).
 * `now` is injectable for deterministic tests. Returns '—' when null.
 */
export function formatRelativeDay(day: string | null, now: Date = new Date()): string {
  if (!day) return '—';
  const then = new Date(day + 'T00:00:00Z');
  if (Number.isNaN(then.getTime())) return '—';
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const diffDays = Math.round((today.getTime() - then.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return 'last week';
  return `${Math.floor(diffDays / 7)} weeks ago`;
}
